/**
 * R5/F6 — Foto del materiale nella Card Materiale (dettaglio richiesta/ordine).
 * Griglia di thumbnail servite in modo AUTENTICATO (fetch con token → blob →
 * object URL, mai URL pubblici). Per i ruoli di scrittura: upload multi-file e
 * cestino, con contatore x/5. Il file fisico non viene mai cancellato lato server
 * (soft-delete della riga).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Trash2, X } from 'lucide-react';
import {
  listFotoOrdine, getFotoObjectUrl, uploadFotoOrdine, deleteFotoOrdine,
  type FotoItem,
} from '../../api/richieste';

const MAX_FOTO = 5;

interface Props {
  ordineId: string;
  canWrite: boolean;
}

export default function MaterialeFotoSection({ ordineId, canWrite }: Props) {
  const { t } = useTranslation();
  const [foto, setFoto] = useState<FotoItem[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const urlsRef = useRef<Record<string, string>>({});

  // Carica lista + object URL. Usata dagli handler (upload/delete): NON in un effect,
  // così il refresh post-azione è libero di fare setState.
  const reload = useCallback(async () => {
    const items = await listFotoOrdine(ordineId);
    const next: Record<string, string> = {};
    for (const it of items) {
      next[it.id] = urlsRef.current[it.id] ?? (await getFotoObjectUrl(ordineId, it.id));
    }
    for (const [pid, u] of Object.entries(urlsRef.current)) {
      if (!next[pid]) URL.revokeObjectURL(u);   // revoca gli URL non più referenziati
    }
    urlsRef.current = next;
    setFoto(items);
    setUrls(next);
  }, [ordineId]);

  // Caricamento iniziale: IIFE inline nell'effect (pattern del repo per il data-fetch).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = await listFotoOrdine(ordineId);
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const it of items) next[it.id] = await getFotoObjectUrl(ordineId, it.id);
        if (cancelled) { for (const u of Object.values(next)) URL.revokeObjectURL(u); return; }
        urlsRef.current = next;
        setFoto(items);
        setUrls(next);
      } catch {
        if (!cancelled) setError(t('richieste.foto.errore_caricamento'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ordineId, t]);

  // Cleanup finale degli object URL al dismount.
  useEffect(() => () => {
    for (const u of Object.values(urlsRef.current)) URL.revokeObjectURL(u);
  }, []);

  async function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    const disponibili = MAX_FOTO - foto.length;
    if (disponibili <= 0) { setError(t('richieste.foto.max_raggiunto', { n: MAX_FOTO })); return; }
    const daCaricare = Array.from(files).slice(0, disponibili);
    setBusy(true);
    try {
      for (const f of daCaricare) {
        try {
          await uploadFotoOrdine(ordineId, f);
        } catch (e: unknown) {
          const st = (e as { response?: { status?: number } })?.response?.status;
          setError(st === 413 ? t('richieste.foto.errore_size')
            : st === 422 ? t('richieste.foto.errore_formato')
            : t('richieste.foto.errore_upload'));
        }
      }
      await reload();
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function rimuovi(id: string) {
    setBusy(true);
    try { await deleteFotoOrdine(ordineId, id); await reload(); }
    catch { setError(t('richieste.foto.errore_delete')); }
    finally { setBusy(false); }
  }

  return (
    <div className="border-t border-stone-mid pt-3 mt-3" data-testid="materiale-foto">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-ink-soft">
          {t('richieste.foto.titolo')} <span className="text-ink-faint">({foto.length}/{MAX_FOTO})</span>
        </span>
        {canWrite && (
          <>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic"
              multiple className="hidden" data-testid="foto-input"
              onChange={(e) => void onFiles(e.target.files)} />
            <button type="button" disabled={busy || foto.length >= MAX_FOTO}
              onClick={() => fileRef.current?.click()} data-testid="foto-aggiungi"
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-sage border border-sage rounded hover:bg-sage-light disabled:opacity-40 disabled:cursor-not-allowed">
              <ImagePlus size={13} /> {t('richieste.foto.aggiungi')}
            </button>
          </>
        )}
      </div>

      {error && <p className="text-xs text-accent-danger mb-2" data-testid="foto-error">{error}</p>}

      {loading ? (
        <p className="text-xs text-ink-faint">{t('common.caricamento_puntini')}</p>
      ) : foto.length === 0 ? (
        <p className="text-xs text-ink-faint italic">{t('richieste.foto.nessuna')}</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {foto.map((f) => (
            <div key={f.id} className="relative group aspect-square" data-testid={`foto-thumb-${f.id}`}>
              {urls[f.id] && (
                <img src={urls[f.id]} alt="" onClick={() => setZoom(urls[f.id])}
                  className="w-full h-full object-cover rounded border border-stone-mid cursor-zoom-in" />
              )}
              {canWrite && (
                <button type="button" onClick={() => void rimuovi(f.id)} disabled={busy}
                  data-testid={`foto-del-${f.id}`}
                  className="absolute top-1 right-1 p-1 bg-white/90 rounded text-accent-danger opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-40">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {zoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setZoom(null)} data-testid="foto-zoom">
          <button type="button" className="absolute top-4 right-4 text-white" onClick={() => setZoom(null)}><X size={24} /></button>
          <img src={zoom} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded" />
        </div>
      )}
    </div>
  );
}
